/**
 * 路由常量定义
 * 集中管理所有路由路径，避免硬编码
 */

export const ROUTES = {
  // 公共路由
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  SHARE: '/share/:token',
  STUDENT: '/student',

  // 考试模块
  EXAM: '/exam',
  EXAM_HISTORY: '/exam/history',
  EXAM_PAPER: '/exam/paper/:paperId',
  EXAM_EDITOR: '/exam/editor/:paperId',
  EXAM_SESSION: '/exam/session/:sessionId',
  EXAM_DIAGNOSIS: '/exam/diagnosis/:sessionId',
  EXAM_EXPLANATION: '/exam/diagnosis/:sessionId/explain/:lessonId',

  // 错题模块
  MISTAKES: '/mistakes',
  MISTAKE_DETAIL: '/mistakes/:mistakeId',

  // 闪卡模块
  FLASHCARDS: '/flashcards',
  FLASHCARD_DECK: '/flashcards/deck/:deckId',
  FLASHCARD_STUDY: '/flashcards/study',

  // AI团队模块
  AI_TEAM: '/ai-team',
  AI_TEAM_DIAGNOSIS: '/ai-team/diagnosis',
  AI_TEAM_COURSES: '/ai-team/courses',
  AI_TEAM_NEW_COURSE: '/ai-team/courses/new',
  AI_TEAM_COURSE_DETAIL: '/ai-team/courses/:courseId',
  AI_TEAM_LESSON_PLAYER: '/ai-team/lessons/:lessonId',
  AI_TEAM_TASKS: '/ai-team/tasks',

  // 自助备考模块
  EXAM_PREP: '/exam-prep',
  EXAM_PREP_NEW: '/exam-prep/new',
  EXAM_PREP_DETAIL: '/exam-prep/:planId',

  // 教师模块
  TEACHER: '/teacher',
  TEACHER_LESSON: '/teacher/:lessonId',
  TEACHER_PRINT: '/teacher/:lessonId/print',

  // 工具模块
  QB_EXTRACT: '/qb/extract',
  TUTOR: '/tutor',
  TUTOR_VISUALIZATION: '/tutor/visualization',
  QUESTION_PLAYER: '/question-player',
  GEOMASTER: '/geomaster',
  XIAOHUA: '/xiaohua',
  KNOWLEDGE_POINTS: '/knowledge-points',
} as const

/**
 * 路由元数据
 * 用于面包屑导航和侧边栏菜单
 */
export interface RouteMeta {
  label: string
  parent?: string
  icon?: string
}

export const ROUTE_META: Record<string, RouteMeta> = {
  [ROUTES.HOME]: { label: '首页', icon: 'Home' },
  [ROUTES.STUDENT]: { label: '学生端', parent: ROUTES.HOME, icon: 'PlayCircle' },

  // 考试模块
  [ROUTES.EXAM]: { label: '考试练习', parent: ROUTES.HOME, icon: 'FileText' },
  [ROUTES.EXAM_HISTORY]: { label: '答题历史', parent: ROUTES.EXAM },
  [ROUTES.EXAM_PAPER]: { label: '试卷详情', parent: ROUTES.EXAM },
  [ROUTES.EXAM_EDITOR]: { label: '试卷编辑', parent: ROUTES.EXAM },
  [ROUTES.EXAM_SESSION]: { label: '答题', parent: ROUTES.EXAM },
  [ROUTES.EXAM_DIAGNOSIS]: { label: '诊断报告', parent: ROUTES.EXAM },
  [ROUTES.EXAM_EXPLANATION]: { label: '试题讲解', parent: ROUTES.EXAM_DIAGNOSIS },

  // 错题模块
  [ROUTES.MISTAKES]: { label: '错题本', parent: ROUTES.HOME, icon: 'BookOpen' },
  [ROUTES.MISTAKE_DETAIL]: { label: '错题详情', parent: ROUTES.MISTAKES },

  // 闪卡模块
  [ROUTES.FLASHCARDS]: { label: '闪卡', parent: ROUTES.HOME, icon: 'Layers' },
  [ROUTES.FLASHCARD_DECK]: { label: '卡组详情', parent: ROUTES.FLASHCARDS },
  [ROUTES.FLASHCARD_STUDY]: { label: '闪卡学习', parent: ROUTES.FLASHCARDS },

  // AI团队模块
  [ROUTES.AI_TEAM]: { label: 'AI学习中心', parent: ROUTES.HOME, icon: 'BrainCircuit' },
  [ROUTES.AI_TEAM_DIAGNOSIS]: { label: '诊断', parent: ROUTES.AI_TEAM },
  [ROUTES.AI_TEAM_COURSES]: { label: '课程', parent: ROUTES.AI_TEAM },
  [ROUTES.AI_TEAM_NEW_COURSE]: { label: '新建课程', parent: ROUTES.AI_TEAM_COURSES },
  [ROUTES.AI_TEAM_COURSE_DETAIL]: { label: '课程详情', parent: ROUTES.AI_TEAM_COURSES },
  [ROUTES.AI_TEAM_LESSON_PLAYER]: { label: '课时学习', parent: ROUTES.AI_TEAM_COURSES },
  [ROUTES.AI_TEAM_TASKS]: { label: '每日任务', parent: ROUTES.AI_TEAM },

  // 自助备考模块
  [ROUTES.EXAM_PREP]: { label: '自助备考', parent: ROUTES.AI_TEAM, icon: 'Target' },
  [ROUTES.EXAM_PREP_NEW]: { label: '新建备考计划', parent: ROUTES.EXAM_PREP },
  [ROUTES.EXAM_PREP_DETAIL]: { label: '备考计划详情', parent: ROUTES.EXAM_PREP },

  // 教师模块
  [ROUTES.TEACHER]: { label: '教师工作区', parent: ROUTES.HOME, icon: 'Presentation' },
  [ROUTES.TEACHER_LESSON]: { label: '课件编辑', parent: ROUTES.TEACHER },
  [ROUTES.TEACHER_PRINT]: { label: '打印预览', parent: ROUTES.TEACHER_LESSON },

  // 工具模块
  [ROUTES.QB_EXTRACT]: { label: '题目提取', parent: ROUTES.HOME, icon: 'Database' },
  [ROUTES.TUTOR]: { label: 'AI辅导', parent: ROUTES.HOME, icon: 'MessageSquare' },
  [ROUTES.TUTOR_VISUALIZATION]: { label: '可视化', parent: ROUTES.TUTOR },
  [ROUTES.QUESTION_PLAYER]: { label: '题目讲解', parent: ROUTES.HOME, icon: 'Presentation' },
  [ROUTES.GEOMASTER]: { label: '几何大师', parent: ROUTES.HOME, icon: 'Shapes' },
  [ROUTES.XIAOHUA]: { label: '小华老师', parent: ROUTES.HOME, icon: 'Paintbrush' },
  [ROUTES.KNOWLEDGE_POINTS]: { label: '知识点', parent: ROUTES.HOME, icon: 'Network' },
}

/**
 * 生成路由路径的工具函数
 * 替换路径中的参数占位符（支持多个相同的参数占位符）
 */
export function generatePath(pattern: string, params: Record<string, string | number>): string {
  let path = pattern
  for (const [key, value] of Object.entries(params)) {
    // 使用全局替换，支持多个相同的参数占位符
    path = path.replace(new RegExp(`:${key}`, 'g'), String(value))
  }
  return path
}
